import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Code2, ExternalLink } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

interface Software {
  id: string;
  name: string;
  description: string;
  website_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const SoftwareManager = () => {
  const [software, setSoftware] = useState<Software[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSoftware, setSelectedSoftware] = useState<Software | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website_url: '',
    is_active: true,
  });

  useEffect(() => {
    fetchSoftware();
  }, []);

  const fetchSoftware = async () => {
    try {
      const { data, error } = await supabase
        .from('software')
        .select('*')
        .order('name');

      if (error) throw error;
      setSoftware(data || []);
    } catch (error) {
      console.error('Error fetching software:', error);
      toast.error('Yazılımlar yüklenirken bir hata oluştu');
    }
  };

  const handleAdd = () => {
    setSelectedSoftware(null);
    setFormData({
      name: '',
      description: '',
      website_url: '',
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const handleEdit = (software: Software) => {
    setSelectedSoftware(software);
    setFormData({
      name: software.name,
      description: software.description || '',
      website_url: software.website_url || '',
      is_active: software.is_active,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`"${name}" yazılımını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      // Check if any forums use this software
      const { data: forums, error: forumError } = await supabase
        .from('forums')
        .select('id, name')
        .eq('software', name)
        .limit(5);

      if (forumError) throw forumError;

      if (forums && forums.length > 0) {
        const forumNames = forums.map(f => f.name).join(', ');
        toast.error(`Bu yazılımı kullanan forumlar var: ${forumNames}. Önce forumların yazılımını değiştirin.`);
        return;
      }

      const { error } = await supabase
        .from('software')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSoftware(software.filter(s => s.id !== id));
      toast.success('Yazılım başarıyla silindi');
    } catch (error) {
      console.error('Error deleting software:', error);
      toast.error('Yazılım silinirken bir hata oluştu');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.name.trim()) {
        toast.error('Yazılım adı boş olamaz');
        setLoading(false);
        return;
      }

      // Check for duplicate names
      let query = supabase
        .from('software')
        .select('id')
        .eq('name', formData.name.trim());

      if (selectedSoftware?.id) {
        query = query.neq('id', selectedSoftware.id);
      }

      const { data: existingSoftware, error: checkError } = await query.maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') throw checkError;
      
      if (existingSoftware) {
        toast.error('Bu yazılım adı zaten kullanılıyor');
        setLoading(false);
        return;
      }

      const softwareData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        website_url: formData.website_url.trim() || null,
        is_active: formData.is_active,
      };

      if (selectedSoftware) {
        // Update existing software
        const { error: updateError } = await supabase
          .from('software')
          .update(softwareData)
          .eq('id', selectedSoftware.id);

        if (updateError) throw updateError;

        // Update forums that use the old name
        if (selectedSoftware.name !== formData.name.trim()) {
          console.log(`Updating forums from "${selectedSoftware.name}" to "${formData.name.trim()}"`);
          const { error: forumUpdateError } = await supabase
            .from('forums')
            .update({ software: formData.name.trim() })
            .eq('software', selectedSoftware.name);

          if (forumUpdateError) {
            console.error('Forum update error:', forumUpdateError);
            throw forumUpdateError;
          } else {
            console.log('Forums updated successfully');
          }
        }

        toast.success('Yazılım başarıyla güncellendi');
      } else {
        // Insert new software
        const { data, error: insertError } = await supabase
          .from('software')
          .insert([softwareData])
          .select()
          .single();

        if (insertError) throw insertError;
        if (!data) throw new Error('Yazılım eklenemedi');

        toast.success('Yazılım başarıyla eklendi');
      }

      setIsModalOpen(false);
      await fetchSoftware();
    } catch (error) {
      console.error('Error saving software:', error);
      toast.error('Yazılım kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getForumCount = async (softwareName: string): Promise<number> => {
    try {
      const { count, error } = await supabase
        .from('forums')
        .select('*', { count: 'exact', head: true })
        .eq('software', softwareName);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error getting forum count:', error);
      return 0;
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Yazılım Yönetimi</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <Plus size={20} />
          Yeni Yazılım Ekle
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {software.map(item => (
          <div
            key={item.id}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                  <Code2 className="text-indigo-600 dark:text-indigo-400" size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {item.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      item.is_active 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {item.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="p-1 text-blue-600 hover:text-blue-800"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => handleDelete(item.id, item.name)}
                  className="p-1 text-red-600 hover:text-red-800"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
              {item.description || 'Açıklama yok'}
            </p>

            {item.website_url && (
              <a
                href={item.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 text-sm mb-4"
              >
                <ExternalLink size={14} />
                Resmi Website
              </a>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Oluşturulma: {new Date(item.created_at).toLocaleDateString('tr-TR')}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

          <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-md w-full mx-4 p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {selectedSoftware ? 'Yazılım Düzenle' : 'Yeni Yazılım Ekle'}
            </Dialog.Title>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Yazılım Adı
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                  placeholder="Örn: Invision Community"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Yazılım hakkında kısa açıklama..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Website URL
                </label>
                <input
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="https://example.com"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Aktif
                </label>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? 'Kaydediliyor...' : (selectedSoftware ? 'Güncelle' : 'Ekle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default SoftwareManager;